import { useContext } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BORDER,
  CHART_POSITIVE,
  MUTED_FG,
  tooltipStyle,
  xAxisProps,
  yAxisProps,
} from "./chartTheme";
import { ChartPreviewContext } from "./ChartPreviewContext";
import MetricChartFrame from "./MetricChartFrame";

interface MetricBarChartProps {
  caption: string;
  data: Array<{ label: string; value: number }>;
  /** Tooltip series label. */
  seriesName: string;
  /** Formats values for the tooltip and data labels (e.g. `${v}%`, `${v} mo`). */
  format: (value: number) => string;
  /** Formats Y-axis ticks; defaults to `format`. */
  formatAxis?: (value: number) => string;
  color?: string;
  /**
   * Optional dashed reference line (e.g. a 100% ceiling). Prefer surfacing the
   * threshold as a Y-axis tick (via `ticks`) over a `label` — only label the
   * line when the annotation says more than the value (e.g. "1% penalty risk").
   */
  reference?: { y: number; label?: string };
  domain?: [number | string, number | string];
  /** Explicit Y-axis ticks — pin these so a reference line's value lands on the axis. */
  ticks?: number[];
  yWidth?: number;
  active?: string;
  breakdown?: string;
  chartType?: string;
}

/**
 * Generic single-series bar chart for rate (%) and duration (months) metrics —
 * anything that isn't a dollar amount. Supports an optional reference line.
 */
const MetricBarChart = ({
  caption,
  data,
  seriesName,
  format,
  formatAxis,
  color = CHART_POSITIVE,
  reference,
  domain,
  ticks,
  yWidth = 44,
  active = "6M",
  breakdown = "No breakdown",
  chartType = "Bar",
}: MetricBarChartProps) => {
  // Thumbnail previews (library cards) are too small for the reference label —
  // keep the dashed line, drop the text.
  const preview = useContext(ChartPreviewContext);

  return (
    <MetricChartFrame caption={caption} chartType={chartType} breakdown={breakdown} active={active}>
      <BarChart data={data} margin={{ top: 16, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
        <XAxis dataKey="label" {...xAxisProps} />
        <YAxis
          tickFormatter={(v) => (formatAxis ?? format)(Number(v))}
          domain={domain}
          ticks={ticks}
          width={yWidth}
          {...yAxisProps}
        />
        <Tooltip
          cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
          contentStyle={tooltipStyle}
          formatter={(value: number) => [format(value), seriesName]}
        />
        {reference && (
          <ReferenceLine
            y={reference.y}
            stroke={MUTED_FG}
            strokeDasharray="4 4"
            label={
              reference.label && !preview
                ? {
                    value: reference.label,
                    position: "insideTopRight",
                    fill: MUTED_FG,
                    fontSize: 11,
                  }
                : undefined
            }
          />
        )}
        <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} isAnimationActive={false}>
          <LabelList
            dataKey="value"
            position="top"
            formatter={(v: number) => format(Number(v))}
            fontSize={11}
            fill={MUTED_FG}
          />
        </Bar>
      </BarChart>
    </MetricChartFrame>
  );
};

export default MetricBarChart;
